import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  Copy,
  Lightning,
  ListChecks,
} from "@phosphor-icons/react";
import { generateHostnames } from "@/lib/api";
import { GEN } from "@/constants/testIds";

export function HostnameGenerator() {
  const [count, setCount] = useState(5);
  const [hostnames, setHostnames] = useState([]);
  const [loading, setLoading] = useState(false);

  const runGenerate = async (n) => {
    setLoading(true);
    try {
      const list = await generateHostnames(n);
      setHostnames(list);
    } catch (e) {
      toast.error("Failed to generate hostnames");
    } finally {
      setLoading(false);
    }
  };

  const regenOne = async (idx) => {
    try {
      const [one] = await generateHostnames(1);
      setHostnames((prev) => prev.map((h, i) => (i === idx ? one : h)));
    } catch (e) {
      toast.error("Failed to regenerate");
    }
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      return false;
    }
  };

  const copyOne = async (name) => {
    const ok = await safeCopy(name);
    if (ok) toast.success("Copied", { description: name });
    else toast.error("Clipboard blocked", { description: name });
  };

  const copyAll = async () => {
    if (!hostnames.length) return;
    const ok = await safeCopy(hostnames.join("\n"));
    if (ok) toast.success(`Copied ${hostnames.length} hostnames`);
    else toast.error("Clipboard blocked");
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left: control panel (col-span-4) */}
      <div className="col-span-12 lg:col-span-4">
        <Card className="border border-[#E5E7EB] rounded-sm shadow-none">
          <div className="p-5 border-b border-[#E5E7EB]">
            <div className="text-[11px] font-mono-tech uppercase tracking-widest text-[#6B7280]">
              01 / CONFIGURE
            </div>
            <h2 className="font-heading font-black text-2xl mt-1 tracking-tight">
              Hostname Generator
            </h2>
            <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
              Compose deterministic three-word hostnames separated by dashes,
              drawn from a curated vocabulary of ~180 friendly tokens.
            </p>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <Label
                htmlFor="count"
                className="text-[11px] font-mono-tech uppercase tracking-widest text-[#4B5563]"
              >
                Batch size
              </Label>
              <Input
                id="count"
                data-testid={GEN.countInput}
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) =>
                  setCount(
                    Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10)))
                  )
                }
                className="mt-1.5 rounded-sm font-mono-tech border-[#D1D5DB] focus-visible:ring-1 focus-visible:ring-[#0052CC]"
              />
              <div className="text-[11px] text-[#6B7280] mt-1">
                1 – 100 hostnames per run
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                data-testid={GEN.generateBtn}
                onClick={() => runGenerate(count)}
                disabled={loading}
                className="rounded-sm bg-[#0052CC] hover:bg-[#0043A6] text-white font-heading font-bold uppercase tracking-wide text-xs flex-1"
              >
                <Lightning size={16} weight="fill" className="mr-1.5" />
                {loading ? "Generating…" : "Generate"}
              </Button>
              <Button
                data-testid={GEN.copyAllBtn}
                onClick={copyAll}
                disabled={!hostnames.length}
                variant="outline"
                className="rounded-sm border-[#111827] text-[#111827] font-heading font-bold uppercase tracking-wide text-xs hover:bg-[#111827] hover:text-white"
              >
                <ListChecks size={16} weight="bold" className="mr-1.5" />
                Copy all
              </Button>
            </div>

            <div className="text-[11px] text-[#6B7280] font-mono-tech leading-relaxed border-t border-[#E5E7EB] pt-4">
              <div className="uppercase tracking-widest mb-1 text-[#111827]">
                Pattern
              </div>
              <span className="text-[#111827]">{"{word}"}</span>
              <span className="text-[#0052CC]">-</span>
              <span className="text-[#111827]">{"{word}"}</span>
              <span className="text-[#0052CC]">-</span>
              <span className="text-[#111827]">{"{word}"}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Right: output display (col-span-8) */}
      <div className="col-span-12 lg:col-span-8">
        <Card className="border border-[#E5E7EB] rounded-sm shadow-none h-full">
          <div className="p-5 border-b border-[#E5E7EB] flex items-center justify-between">
            <div>
              <div className="text-[11px] font-mono-tech uppercase tracking-widest text-[#6B7280]">
                02 / OUTPUT
              </div>
              <div className="font-heading font-bold text-lg mt-0.5">
                {hostnames.length
                  ? `${hostnames.length} hostname${hostnames.length > 1 ? "s" : ""}`
                  : "Awaiting generation"}
              </div>
            </div>
            {hostnames.length > 0 && (
              <div className="text-[11px] font-mono-tech text-[#6B7280]">
                Ready to copy · click any row to duplicate
              </div>
            )}
          </div>

          <div
            data-testid={GEN.outputList}
            className="p-5 min-h-[440px] tech-scroll overflow-auto"
          >
            {hostnames.length === 0 ? (
              <div className="h-[380px] grid place-items-center text-center">
                <div>
                  <div className="text-6xl font-heading font-black text-[#E5E7EB] tracking-tight">
                    3-WORD
                  </div>
                  <div className="text-[13px] text-[#6B7280] mt-2 max-w-xs mx-auto">
                    Configure a batch size and press{" "}
                    <span className="font-mono-tech text-[#111827]">Generate</span>{" "}
                    to compose fresh hostnames.
                  </div>
                </div>
              </div>
            ) : (
              <ul className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
                {hostnames.map((h, i) => (
                  <li
                    key={`${h}-${i}`}
                    className="row-hover flex items-center justify-between px-4 py-2.5 group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="text-[10px] font-mono-tech text-[#9CA3AF] w-6 tabular-nums">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div className="font-mono-tech text-[15px] text-[#111827] truncate">
                        {h}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <Button
                        data-testid={GEN.regenRowBtn(i)}
                        size="sm"
                        variant="ghost"
                        onClick={() => regenOne(i)}
                        className="h-7 px-2 rounded-sm text-[#4B5563] hover:bg-[#F1F3F5]"
                        title="Regenerate"
                      >
                        <ArrowsClockwise size={14} weight="bold" />
                      </Button>
                      <Button
                        data-testid={GEN.copyRowBtn(i)}
                        size="sm"
                        variant="ghost"
                        onClick={() => copyOne(h)}
                        className="h-7 px-2 rounded-sm text-[#4B5563] hover:bg-[#F1F3F5]"
                        title="Copy"
                      >
                        <Copy size={14} weight="bold" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
